import React from 'react';
import { Shape } from 'react-konva';
import Konva from 'konva';
import { useAppSelector } from '../../hooks/redux';
import { facilityTypeColor } from '../../utils/facility';
import { hexToRgba } from '../../utils/colorConversion';

/**
 * Renders facility-analysis regions as a translucent zone overlay in a single
 * non-interactive Konva.Shape (one node, viewport-culled), mirroring the
 * FloorPlan rendering model. Hover/selection highlights are drawn separately in
 * the interactive layer (see Canvas) so this base layer stays static.
 */
const FacilityZones: React.FC = () => {
  const regions = useAppSelector((s) => s.facility.regions);
  const visible = useAppSelector((s) => s.facility.visible);
  const visibleTypes = useAppSelector((s) => s.facility.visibleTypes);
  const minConfidence = useAppSelector((s) => s.facility.minConfidence);
  const selectedId = useAppSelector((s) => s.facility.selectedId);

  const sceneFunc = React.useCallback(
    (context: Konva.Context, shape: Konva.Shape) => {
      if (regions.length === 0) return;
      const stage = shape.getStage();
      if (!stage) return;

      const ctx = context._context;
      const scale = stage.scaleX() || 1;
      const pos = stage.position();

      const viewLeft = -pos.x / scale;
      const viewTop = -pos.y / scale;
      const viewRight = viewLeft + stage.width() / scale;
      const viewBottom = viewTop + stage.height() / scale;

      const lineW = 1 / scale;

      for (let i = 0; i < regions.length; i++) {
        const r = regions[i];
        if (visibleTypes[r.type] === false) continue;
        if (r.confidence < minConfidence) continue;

        // Cull regions fully outside the viewport.
        if (
          r.x + r.width < viewLeft ||
          r.x > viewRight ||
          r.y + r.height < viewTop ||
          r.y > viewBottom
        ) {
          continue;
        }

        const color = facilityTypeColor(r.type);
        const isSelected = r.id === selectedId;

        ctx.fillStyle = hexToRgba(color, isSelected ? 0.28 : 0.12);
        ctx.fillRect(r.x, r.y, r.width, r.height);

        ctx.strokeStyle = color;
        ctx.lineWidth = isSelected ? 2.5 / scale : lineW;
        ctx.strokeRect(r.x, r.y, r.width, r.height);
      }
    },
    [regions, visibleTypes, minConfidence, selectedId]
  );

  if (!visible || regions.length === 0) return null;

  return <Shape sceneFunc={sceneFunc} listening={false} perfectDrawEnabled={false} />;
};

export default React.memo(FacilityZones);
